import { useEffect } from "react";
import { toast } from "react-toastify";
import reduxStore from "../../configureEnvStore";
import { setUploadQueue } from "../../reducers/offline-mode-reducer";
import { indexedDBService } from "./indexedDBService";
import { useSelector } from "react-redux";
import { offlineModeService } from "./offlienModeService";
import { getActiveRoom } from "../Twilio/twilioCallUtility";
import { isOfflinePhotoCaptureFeatureAvailable } from "../../shared/utility";
import CustomToast from "../UI/customToast";
import RetryOfflineFailedImagesToastModal from "./RetryOfflineFailedImagesToastModal";
import { SCHEDULE_SESSION_ERROR_CODE } from "../../constants/Constants";
import { LogFirebase } from "../../services/firestore";

const OFFLINE_FAILED_IMAGE_RETRY_TOAST_ID = "OFFLINE_FAILED_IMAGE_RETRY_TOAST_ID";

const OfflineMode = () => {
    const { isRoomConnected, isRemoteParticipantJoined } = useSelector((state) => state.commonReducer);
    const { isLocalUserOffline } = useSelector((state) => state.batteryNetworkInfo);
    const { isOfflineModeActive, uploadQueue } = useSelector((state) => state.offlineMode);
    const { joinMeetingError } = useSelector((state) => state.joinMeeting);

    useEffect(() => {
        if (joinMeetingError?.message && joinMeetingError?.code !== SCHEDULE_SESSION_ERROR_CODE && isOfflineModeActive) {
            LogFirebase("OfflineMode", "reconnect_button_joinmeeting_error_offlineMode", {
                error: joinMeetingError?.message,
            });
            CustomToast.error(joinMeetingError.message);
        }
    }, [joinMeetingError, isOfflineModeActive]);

    const handleShowRetryToast = async (uploadQueue = []) => {
        const totalSuccess = uploadQueue.filter((item) => item.status === "success")?.length;
        const totalFailed = uploadQueue.filter((item) => item.status === "failed")?.length;
        console.log("uploadQueue__Success:", totalSuccess);
        console.log("uploadQueue__Failed:", totalFailed);
        const storedImages = await indexedDBService.getAllImages();
        LogFirebase("OfflineMode", "indexed_DB_store_image_count_handleShowRetryToast", {
            count: storedImages?.length,
            totalFailed: totalFailed,
            totalSuccess: totalSuccess,
        });
        if (totalFailed > 0 && storedImages?.length > 0) {
            LogFirebase("OfflineMode", "Retry_Offline_Failed_Images_Toast_show", "true");
            const retryToastTimeout = setTimeout(() => {
                clearTimeout(retryToastTimeout);
                CustomToast.default(<RetryOfflineFailedImagesToastModal key={OFFLINE_FAILED_IMAGE_RETRY_TOAST_ID} />, {
                    toastId: OFFLINE_FAILED_IMAGE_RETRY_TOAST_ID,
                    hideProgressBar: true,
                    autoClose: false,
                    onClose: () => {
                        // do nothing
                    },
                    data: {
                        storedImages: storedImages?.length,
                        handleActionFunction: handleStartUpload,
                    },
                });
            }, 1000);
        }
    };

    useEffect(() => {
        if (uploadQueue.length > 0) {
            const allCompleted = uploadQueue.every((item) => item.status === "success" || item.status === "failed");
            // ✅ All uploads are completed
            if (allCompleted) {
                handleShowRetryToast(uploadQueue);
            }
        }
    }, [uploadQueue]);

    useEffect(() => {
        async function checkIndexedDBImages() {
            const images = await indexedDBService.getAllImages();
            LogFirebase("OfflineMode", "indexed_DB_store_image_count_isLocalUserOffline_hook", { count: images?.length });
            return images.length > 0;
        }
        if (!isOfflinePhotoCaptureFeatureAvailable()) {
            return; // Feature not available, exit early
        }
        if (!isLocalUserOffline && !isOfflineModeActive && isOfflinePhotoCaptureFeatureAvailable() && !offlineModeService.isUploading) {
            checkIndexedDBImages().then((imagesAvailable) => {
                if (imagesAvailable) {
                    LogFirebase("OfflineMode", "isLocalUserOffline_hook", "Local user is Online. Starting upload process.");
                    console.log("Local user is offline. Starting upload process.");
                    handleStartUpload();
                }
            });
        } else {
            LogFirebase("OfflineMode", "isLocalUserOffline_hook", "Local user is Offline. upload stop.");
            console.log("Local user is Offline. No upload needed.");
        }
    }, [isLocalUserOffline]);

    useEffect(() => {
        const room = getActiveRoom();
        if (!isOfflinePhotoCaptureFeatureAvailable()) {
            return; // Feature not available, exit early
        }
        if (room?.participants?.size > 0 && isRemoteParticipantJoined) {
            // already remote participant joined
            let timeout = setTimeout(() => {
                clearTimeout(timeout);
                LogFirebase("OfflineMode", "RemoteParticipant_Joined_room", "check_is_any_image_stored_in_indexedDB");
                handleStartUpload();
            }, 2000);
        } else if (isRoomConnected && !isRemoteParticipantJoined && room?.participants?.size === 0) {
            // room connected but no remote participant joined
            LogFirebase("OfflineMode", "RemoteParticipant_not_Joined", "check_is_any_image_stored_in_indexedDB");
            handleStartUpload();
        }
    }, [isRoomConnected, isRemoteParticipantJoined]);

    const uploadImageToServer = async (image) => {
        await new Promise((resolve) => setTimeout(resolve, 100));
        console.log("Uploading image:", image);
        await offlineModeService.uploadPhoto(image);
    };

    const handleStartUpload = async () => {
        if (offlineModeService.isUploading) return;
        const images = await indexedDBService.getAllImages();
        LogFirebase("OfflineMode", "indexed_DB_store_image_count_handleStartUpload", { count: images?.length });
        offlineModeService.uploadingCount = images.length;
        console.log("Stored images:", images);

        if (images.length === 0) return;

        if (toast.isActive(OFFLINE_FAILED_IMAGE_RETRY_TOAST_ID)) {
            toast.dismiss(OFFLINE_FAILED_IMAGE_RETRY_TOAST_ID); // Remove failed image retry toasts
        }
        offlineModeService.resetofflineUploadVariables(); // reset offline service variables
        offlineModeService.imageDataList = images; // set total images
        offlineModeService.initializeUploadStatus(); // starting toast
        LogFirebase("OfflineMode", "handleStartUpload", "true");

        let currentUploadQueue = images.map((img) => ({ ...img, status: "pending" }));
        reduxStore.dispatch(setUploadQueue(currentUploadQueue));

        for (let i = 0; i < images.length; i++) {
            const image = images[i];

            // 🔄 Mark as uploading
            currentUploadQueue = currentUploadQueue.map((item, index) => (index === i ? { ...item, status: "uploading" } : item));
            reduxStore.dispatch(setUploadQueue(currentUploadQueue));

            try {
                LogFirebase("OfflineMode", "images_upload_to_server_start", { index: i, ID: image?.id, fileName: image?.fileName });
                await uploadImageToServer(image);

                // ✅ Mark as success
                currentUploadQueue = currentUploadQueue.map((item, index) => (index === i ? { ...item, status: "success" } : item));
                reduxStore.dispatch(setUploadQueue(currentUploadQueue));

                LogFirebase("OfflineMode", "Remove_image_from_indexedDB_store", { index: i, ID: image?.id });

                // 🗑️ Remove from IndexedDB
                await indexedDBService.deleteImage(image.id);
            } catch (error) {
                console.error("Upload failed:", error);
                LogFirebase("OfflineMode", "images_upload_to_server_failed", { index: i, ID: image.id, error: error?.message });
                // ❌ Mark as failed
                currentUploadQueue = currentUploadQueue.map((item, index) =>
                    index === i ? { ...item, status: "failed", error: error.message } : item
                );
                reduxStore.dispatch(setUploadQueue(currentUploadQueue));
            }
        }
    };

    return null;
};

export default OfflineMode;
